import { useState, FormEvent } from 'react';
import { useApi } from '../contexts/ApiContext';
import { RegistrarAccountOption, Tag } from '../types';
import Modal from './ui/Modal';

interface Props {
  accounts: RegistrarAccountOption[];
  tags: Tag[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddDomainModal({
  accounts,
  tags,
  onClose,
  onSuccess,
}: Props) {
  const { fetchApi } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [domainName, setDomainName] = useState('');
  const [registrarId, setRegistrarId] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [whoisPrivacy, setWhoisPrivacy] = useState(false);
  const [status, setStatus] = useState('active');
  const [nameservers, setNameservers] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await fetchApi('/domains', {
        method: 'POST',
        body: JSON.stringify({
          domain_name: domainName,
          registrar_id: registrarId,
          expiry_date: expiryDate || undefined,
          auto_renew: autoRenew || undefined,
          whois_privacy: whoisPrivacy,
          status,
          nameservers: nameservers
            ? nameservers.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)
            : undefined,
          notes: notes || undefined,
          tag_ids: selectedTags.length > 0 ? selectedTags : undefined,
        }),
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create domain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add Domain"
      size="lg"
      onClose={onClose}
      dismissible={!loading}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={loading}>
            Cancel
          </button>
          <button type="submit" form="add-domain-form" disabled={loading} className="btn btn-primary">
            {loading ? 'Creating...' : 'Create Domain'}
          </button>
        </>
      }
    >
        <form id="add-domain-form" onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="domainName" className="label">
                Domain Name *
              </label>
              <input
                id="domainName"
                type="text"
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                placeholder="example.com"
                className="input"
                required
              />
            </div>

            <div>
              <label htmlFor="registrar" className="label">
                Account *
              </label>
              <select
                id="registrar"
                value={registrarId}
                onChange={(e) => setRegistrarId(e.target.value)}
                className="input"
                required
              >
                <option value="">Select an account</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} ({a.registrar_name})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiryDate" className="label">
                  Expiry Date
                </label>
                <input
                  id="expiryDate"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label htmlFor="status" className="label">
                  Status
                </label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="locked">Locked</option>
                  <option value="pending_transfer">Pending Transfer</option>
                </select>
              </div>
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-slate-700">Auto-Renew</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={whoisPrivacy}
                  onChange={(e) => setWhoisPrivacy(e.target.checked)}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-slate-700">WHOIS Privacy</span>
              </label>
            </div>

            <div>
              <label htmlFor="nameservers" className="label">
                Nameservers
              </label>
              <textarea
                id="nameservers"
                value={nameservers}
                onChange={(e) => setNameservers(e.target.value)}
                placeholder="ns1.example.com&#10;ns2.example.com"
                rows={3}
                className="input"
              />
              <p className="text-xs text-slate-500 mt-1">
                One per line, or comma-separated
              </p>
            </div>

            <div>
              <label className="label">Tags</label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <label
                    key={tag.id}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm cursor-pointer border ${
                      selectedTags.includes(tag.id)
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTags([...selectedTags, tag.id]);
                        } else {
                          setSelectedTags(
                            selectedTags.filter((id) => id !== tag.id)
                          );
                        }
                      }}
                      className="sr-only"
                    />
                    <span
                      className="w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="notes" className="label">
                Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="input"
              />
            </div>
          </div>
        </form>
    </Modal>
  );
}
